import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../../firebase/firebase";
import { Link } from "react-router-dom";
import SignUp from "./SignUp";
import ForgotPassword from "./ForgotPassword";
import LanguageSelector from "../language/LanguageSelector";
import { useTranslation } from "../../hooks/useTranslation";
import "./Auth.css";

/**
 * Simple login page - no extra marketing content.
 * This is the dedicated /login route.
 */
const LoginPage = () => {
  const t = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [authView, setAuthView] = useState("login"); // 'login', 'signup', 'forgot-password'
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Success - user will be redirected by AuthContext
    } catch (error) {
      console.error("Login error:", error);
      switch (error.code) {
        case "auth/user-not-found":
          setError(
            t("landingPage.auth.login.errors.userNotFound", "User not found")
          );
          break;
        case "auth/wrong-password":
          setError(
            t("landingPage.auth.login.errors.wrongPassword", "Wrong password")
          );
          break;
        case "auth/invalid-email":
          setError(
            t("landingPage.auth.login.errors.invalidEmail", "Invalid email")
          );
          break;
        case "auth/user-disabled":
          setError(
            t("landingPage.auth.login.errors.userDisabled", "User disabled")
          );
          break;
        case "auth/too-many-requests":
          setError(
            t(
              "landingPage.auth.login.errors.tooManyRequests",
              "Too many requests"
            )
          );
          break;
        default:
          setError(t("landingPage.auth.login.errors.default", "Login failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError("");

    try {
      await signInWithPopup(auth, googleProvider);
      // Success - user will be redirected by AuthContext
    } catch (error) {
      console.error("Google sign-in error:", error);
      switch (error.code) {
        case "auth/popup-closed-by-user":
          setError(
            t("landingPage.auth.login.errors.popupClosed", "Popup closed")
          );
          break;
        case "auth/popup-blocked":
          setError(
            t("landingPage.auth.login.errors.popupBlocked", "Popup blocked")
          );
          break;
        case "auth/cancelled-popup-request":
          setError(t("landingPage.auth.login.errors.cancelled", "Cancelled"));
          break;
        default:
          setError(
            t(
              "landingPage.auth.login.errors.googleFailed",
              "Google sign-in failed"
            )
          );
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const renderLoginForm = () => (
    <>
      <h2>{t("landingPage.auth.login.title", "Sign In")}</h2>
      <p className="auth-subtitle">
        {t("landingPage.auth.login.subtitle", "Welcome back!")}
      </p>

      {error && <div className="error-message">{error}</div>}

      <button
        type="button"
        onClick={handleGoogleSignIn}
        className="auth-button google"
        disabled={googleLoading || loading}
      >
        {googleLoading
          ? t("landingPage.auth.login.signingIn", "Signing in...")
          : t(
              "landingPage.auth.login.continueWithGoogle",
              "Continue with Google"
            )}
      </button>

      {!showEmailForm ? (
        <>
          <div className="auth-divider">
            <span>{t("landingPage.auth.login.or", "or")}</span>
          </div>

          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="link-button"
            style={{
              width: "100%",
              padding: "0.75rem",
              marginTop: "0.5rem",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
            disabled={googleLoading || loading}
          >
            {t(
              "landingPage.auth.login.continueWithEmail",
              "Continue with Email"
            )}
          </button>
        </>
      ) : (
        <>
          <div className="auth-divider">
            <span>{t("landingPage.auth.login.or", "or")}</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">
                {t("landingPage.auth.login.email", "Email")}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t(
                  "landingPage.auth.login.emailPlaceholder",
                  "Enter your email"
                )}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                {t("landingPage.auth.login.password", "Password")}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t(
                  "landingPage.auth.login.passwordPlaceholder",
                  "Enter your password"
                )}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="auth-button primary"
              disabled={loading}
            >
              {loading
                ? t("landingPage.auth.login.signingIn", "Signing in...")
                : t("landingPage.auth.login.signIn", "Sign In")}
            </button>
          </form>
        </>
      )}

      <div className="auth-links">
        <button
          type="button"
          className="link-button"
          onClick={() => setAuthView("forgot-password")}
          disabled={loading}
        >
          {t("landingPage.auth.login.forgotPassword", "Forgot password?")}
        </button>
      </div>

      <div className="auth-switch">
        <p>
          {t("landingPage.auth.login.noAccount", "Don't have an account?")}{" "}
          <button
            type="button"
            className="link-button"
            onClick={() => setAuthView("signup")}
            disabled={loading || googleLoading}
          >
            {t("landingPage.auth.login.signUp", "Sign Up")}
          </button>
        </p>
      </div>
    </>
  );

  return (
    <div className="login-page-container">
      {/* Language Selector */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 1000,
        }}
      >
        <LanguageSelector />
      </div>

      <div className="login-page-card">
        {/* Logo */}
        <div className="login-logo">
          <Link to="/">
            <img
              src="/images/stoiccredit-logo.png"
              alt="StoicCredit"
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                border: "3px solid #d4a574",
              }}
            />
          </Link>
          <h1
            style={{
              fontSize: "24px",
              color: "var(--primary)",
              marginTop: "10px",
            }}
          >
            StoicCredit
          </h1>
        </div>

        {/* Auth Tabs */}
        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab ${authView === "login" ? "active" : ""}`}
            onClick={() => {
              setAuthView("login");
              setShowEmailForm(false);
            }}
          >
            {t("landingPage.auth.tabs.signIn", "Sign In")}
          </button>
          <button
            type="button"
            className={`auth-tab ${authView === "signup" ? "active" : ""}`}
            onClick={() => {
              setAuthView("signup");
              setShowEmailForm(false);
            }}
          >
            {t("landingPage.auth.tabs.signUp", "Sign Up")}
          </button>
          <button
            type="button"
            className={`auth-tab ${
              authView === "forgot-password" ? "active" : ""
            }`}
            onClick={() => {
              setAuthView("forgot-password");
              setShowEmailForm(false);
            }}
          >
            {t("landingPage.auth.tabs.recover", "Recover")}
          </button>
        </div>

        {/* Auth Content */}
        <div className="auth-content">
          {authView === "login" && renderLoginForm()}
          {authView === "signup" && (
            <>
              <h2>{t("landingPage.auth.signup.title", "Create Account")}</h2>
              <p className="auth-subtitle">
                {t(
                  "landingPage.auth.signup.subtitle",
                  "Start building your credit today"
                )}
              </p>
              <SignUp
                onSwitchToLogin={() => setAuthView("login")}
                embedded={true}
              />
            </>
          )}
          {authView === "forgot-password" && (
            <>
              <h2>
                {t("landingPage.auth.forgotPassword.title", "Reset Password")}
              </h2>
              <p className="auth-subtitle">
                {t(
                  "landingPage.auth.forgotPassword.subtitle",
                  "We'll send you a reset link"
                )}
              </p>
              <ForgotPassword
                onSwitchToLogin={() => setAuthView("login")}
                embedded={true}
              />
            </>
          )}
        </div>

        {/* Back to landing */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Link
            to="/"
            style={{
              color: "var(--primary-light)",
              textDecoration: "none",
              fontSize: "14px",
            }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
